import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Volume2, VolumeX, FolderKanban, Globe, Users, Hash, AtSign, MessageCircle, PhoneMissed } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { SoundSelector } from '@/components/sound-selector';
import type { NotificationSound } from '@shared/schema';

interface ChatAudioSettings {
  project?: number | null;
  global?: number | null;
  topic?: number | null;
  direct?: number | null;
}

interface UserPreferences {
  chatAudioSettings?: ChatAudioSettings;
  showMissedCallPopups?: boolean;
}

interface ChatDockSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ChatSoundCategory = 'project' | 'global' | 'topic' | 'direct';

const CATEGORIES: { key: ChatSoundCategory; label: string; icon: React.ReactNode; description: string }[] = [
  { key: 'project', label: 'Project Chats', icon: <FolderKanban className="h-4 w-4" />, description: 'Left panel project conversations' },
  { key: 'global', label: 'Global Chat', icon: <Globe className="h-4 w-4" />, description: 'Company-wide announcements' },
  { key: 'direct', label: 'Direct Messages', icon: <Users className="h-4 w-4" />, description: 'Private 1:1 conversations' },
  { key: 'topic', label: 'Topic Channels', icon: <Hash className="h-4 w-4" />, description: 'Topic-based discussions' },
];

export function ChatDockSettingsDialog({ open, onOpenChange }: ChatDockSettingsDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [localSettings, setLocalSettings] = useState<ChatAudioSettings>({});
  
  const { data: preferences } = useQuery<UserPreferences>({
    queryKey: ['/api/user/preferences'],
    enabled: open,
  });
  
  const { data: sounds = [] } = useQuery<NotificationSound[]>({
    queryKey: ['/api/notification-sounds'],
    enabled: open,
  });
  
  useEffect(() => {
    if (open && preferences) {
      const current = preferences.chatAudioSettings || {};
      setLocalSettings({
        project: current.project ?? null,
        global: current.global ?? null,
        topic: current.topic ?? null,
        direct: current.direct ?? null,
      });
    }
  }, [open, preferences]);
  
  const updateMutation = useMutation({
    mutationFn: async (settings: ChatAudioSettings) => {
      await apiRequest('PATCH', '/api/user/preferences', {
        chatAudioSettings: settings,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/preferences'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/audio-settings'] });
      toast({
        title: 'Settings saved',
        description: 'Chat notification sounds updated',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save settings',
        variant: 'destructive',
      });
    },
  });
  
  const playPreview = (soundId: number) => {
    const sound = sounds.find(s => s.id === soundId);
    if (sound?.directUrl) {
      const audio = new Audio(sound.directUrl);
      audio.play().catch(() => {});
    }
  };
  
  const handleChange = (category: ChatSoundCategory, value: string) => {
    const soundId = value === 'none' ? null : parseInt(value, 10);
    const newSettings = { ...localSettings, [category]: soundId };
    setLocalSettings(newSettings);
    
    if (soundId) {
      playPreview(soundId);
    }
  };
  
  const handleSave = () => {
    const fullSettings: ChatAudioSettings = {
      project: localSettings.project ?? null,
      global: localSettings.global ?? null,
      topic: localSettings.topic ?? null,
      direct: localSettings.direct ?? null,
    };
    updateMutation.mutate(fullSettings);
  };
  
  const getSoundName = (soundId: number | undefined | null) => {
    if (!soundId) return 'Silent';
    const sound = sounds.find(s => s.id === soundId);
    return sound?.name || 'Unknown';
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white">Chat Settings</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div>
            <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
              <Volume2 className="h-4 w-4" />
              Notification Sounds
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Choose different sounds for each chat category. Changes preview automatically.
            </p>
            
            <div className="space-y-4">
              {CATEGORIES.map(({ key, label, icon, description }) => (
                <div key={key} className="flex items-center justify-between gap-4 p-3 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="text-gray-400">
                      {icon}
                    </div>
                    <div className="min-w-0">
                      <Label className="text-sm text-white">{label}</Label>
                      <p className="text-xs text-gray-500 truncate">{description}</p>
                    </div>
                  </div>
                  
                  <Select
                    value={localSettings[key]?.toString() || 'none'}
                    onValueChange={(value) => handleChange(key, value)}
                  >
                    <SelectTrigger className="w-40 bg-gray-800 border-gray-600 text-white">
                      <SelectValue>
                        <span className="flex items-center gap-2">
                          {localSettings[key] ? (
                            <Volume2 className="h-3 w-3 text-blue-400" />
                          ) : (
                            <VolumeX className="h-3 w-3 text-gray-500" />
                          )}
                          <span className="truncate text-xs">{getSoundName(localSettings[key])}</span>
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600">
                      <SelectItem value="none" className="text-gray-300">
                        <span className="flex items-center gap-2">
                          <VolumeX className="h-3 w-3" />
                          Silent
                        </span>
                      </SelectItem>
                      {sounds.map((sound) => (
                        <SelectItem key={sound.id} value={sound.id.toString()} className="text-gray-300">
                          <span className="flex items-center gap-2">
                            <Volume2 className="h-3 w-3 text-blue-400" />
                            {sound.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-4">
            <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
              <Volume2 className="h-4 w-4" />
              Global Chat Sounds
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              These sounds apply globally across all chat types. Changes save automatically.
            </p>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <AtSign className="h-4 w-4 text-gray-400" />
                  <div>
                    <Label className="text-sm text-white">@ Mentions</Label>
                    <p className="text-xs text-gray-500">When someone tags you in a message</p>
                  </div>
                </div>
                <SoundSelector soundType="soundMentions" label="@" />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <MessageCircle className="h-4 w-4 text-gray-400" />
                  <div>
                    <Label className="text-sm text-white">Unread Chats</Label>
                    <p className="text-xs text-gray-500">When new messages arrive in your channels</p>
                  </div>
                </div>
                <SoundSelector soundType="soundUnreadChats" label="" />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <PhoneMissed className="h-4 w-4 text-gray-400" />
                  <div>
                    <Label className="text-sm text-white">Missed-call popups</Label>
                    <p className="text-xs text-gray-500">Show a coalesced in-app toast when you miss a call (silent; the unread badge always still appears)</p>
                  </div>
                </div>
                <Switch
                  data-testid="switch-show-missed-call-popups"
                  checked={preferences?.showMissedCallPopups ?? true}
                  onCheckedChange={(checked) => {
                    apiRequest('PATCH', '/api/user/preferences', { showMissedCallPopups: checked })
                      .then(() => queryClient.invalidateQueries({ queryKey: ['/api/user/preferences'] }))
                      .catch((error: any) => {
                        toast({
                          title: 'Error',
                          description: error?.message || 'Failed to update preference',
                          variant: 'destructive',
                        });
                      });
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
